import { useEffect, useMemo, useRef, useState } from 'react';

// Dropdown with a checkbox per option. `value` is an array of option values (strings); `options`
// is [{ value, label, count? }]. Features: type-to-search, select/clear the (filtered) options,
// removable chips under the trigger, per-option counts, closes on outside click / Escape.
export default function MultiSelect({
  label,
  placeholder = 'Select…',
  options,
  value,
  onChange,
  disabled = false,
  className = '',
  searchable,
  showChips = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selected = options.filter((o) => value.includes(o.value));
  const showSearch = searchable ?? options.length > 6;
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? options.filter((o) => String(o.label).toLowerCase().includes(term)) : options;
  }, [options, query]);

  const summary = selected.length === 0 ? placeholder : selected.length === 1 ? selected[0].label : `${selected.length} selected`;

  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const visibleValues = visible.map((o) => o.value);
  const allVisibleSelected = visible.length > 0 && visibleValues.every((v) => value.includes(v));
  const toggleVisible = () =>
    onChange(allVisibleSelected ? value.filter((v) => !visibleValues.includes(v)) : Array.from(new Set([...value, ...visibleValues])));

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>}
      <div className="flex items-stretch">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={`flex w-full items-center justify-between gap-2 border bg-white px-3 py-2 text-left text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-400 ${
            selected.length ? 'rounded-l-md border-primary' : 'rounded-md border-gray-300'
          }`}
        >
          <span className={`truncate ${selected.length ? 'font-medium' : 'text-gray-500'}`}>{summary}</span>
          <span className="text-xs text-gray-400">▾</span>
        </button>
        {selected.length > 0 && !disabled && (
          <button
            type="button"
            onClick={() => onChange([])}
            title="Clear"
            aria-label={`Clear ${label || placeholder}`}
            className="rounded-r-md border border-l-0 border-primary bg-white px-2 text-gray-400 hover:text-primary"
          >
            ✕
          </button>
        )}
      </div>

      {showChips && selected.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selected.slice(0, 4).map((o) => (
            <span key={o.value} className="inline-flex max-w-[11rem] items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
              <span className="truncate">{o.label}</span>
              <button type="button" onClick={() => toggle(o.value)} aria-label={`Remove ${o.label}`} className="hover:text-gray-900">
                ×
              </button>
            </span>
          ))}
          {selected.length > 4 && <span className="px-1 text-xs text-gray-500">+{selected.length - 4} more</span>}
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[16rem] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          {showSearch && (
            <div className="border-b border-gray-100 p-2">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No options</div>
          ) : visible.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-1.5 text-xs">
                <button type="button" className="text-primary hover:underline" onClick={toggleVisible}>
                  {allVisibleSelected ? (query ? 'Clear shown' : 'Clear all') : query ? 'Select shown' : 'Select all'}
                </button>
                <span className="text-gray-400">{selected.length} selected</span>
              </div>
              <div className="max-h-64 overflow-y-auto py-1">
                {visible.map((o) => (
                  <label key={o.value} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                    <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
                    <span className="flex-1">{o.label}</span>
                    {o.count !== undefined && <span className="text-xs text-gray-400">{o.count}</span>}
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
